/**
 * HTTPの実行手段だけを差し替えるための境界。
 * 実APIとfixtureで同じApiClientのコード経路（problem+json解釈、pagination、SSE解析）を通すために置く。
 * fixtureは「開発・テストで明示的に選ぶ」ものであり、実API障害時の自動fallbackには使わない。
 */

export type TransportMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export interface TransportRequest {
  method: TransportMethod;
  /** '/v1/...' から始まるパス。query は params で渡す。 */
  path: string;
  params?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
  /** JSON body。form と同時指定しない。 */
  json?: unknown;
  /** multipart body。json・bodyと同時指定しない。 */
  form?: FormData;
  /** 生のbyte列（ファイル本体）。json・formと同時指定しない。 */
  body?: Blob;
  /** bodyのContent-Type。省略時はapplication/octet-stream。 */
  contentType?: string;
  signal?: AbortSignal;
}

export interface TransportResponse {
  status: number;
  header(name: string): string | null;
  text(): Promise<string>;
  bytes(): Promise<Uint8Array>;
}

export interface TransportStream {
  status: number;
  header(name: string): string | null;
  /** UTF-8 decode 済みの文字列断片。chunk境界は呼び出し側のSseParserが吸収する。 */
  chunks(): AsyncIterableIterator<string>;
  close(): void;
}

export interface Transport {
  send(request: TransportRequest): Promise<TransportResponse>;
  openStream(request: TransportRequest): Promise<TransportStream>;
}

/**
 * Access Tokenの取得元。Cognitoの詳細はauth/に閉じ、transportは受け取ったtokenを付けるだけにする。
 * FetchTransportへ渡さなければAuthorizationを付けない（fixtureと認証無効のlocal）。
 */
export interface AccessTokenProvider {
  /** 取れなければnullを返し、投げない。forceRefreshはRefresh Tokenで取り直す。 */
  getAccessToken(options?: { forceRefresh?: boolean }): Promise<string | null>;
  /** refreshしても401が続いたときに呼ぶ。ログインへ遷移を始める。 */
  reauthenticate(): Promise<void>;
}

export function buildUrl(base: string, path: string, params?: TransportRequest['params']): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return `${base}${path}${query ? `?${query}` : ''}`;
}

/**
 * ブラウザのfetchを使う実装。baseUrlが空ならUI serviceのproxy（/v1/* と /healthz）へ相対パスで送り、
 * 静的配信ではビルド時に埋めたAPIの別オリジンへ直接送る。
 */
export class FetchTransport implements Transport {
  constructor(
    private readonly baseUrl = '',
    private readonly auth?: AccessTokenProvider,
  ) {}

  async send(request: TransportRequest): Promise<TransportResponse> {
    const response = await this.authorized((token) =>
      fetch(buildUrl(this.baseUrl, request.path, request.params), {
        method: request.method,
        headers: { ...this.headers(request), ...bearer(token) },
        body: this.body(request),
        signal: request.signal,
        // 認証はBearerヘッダで送り、cookieは使わない。APIが別オリジンでもcredentialsを許可させずに済む。
        credentials: 'omit',
        redirect: 'error',
      }),
    );
    return {
      status: response.status,
      header: (name) => response.headers.get(name),
      text: () => response.text(),
      bytes: async () => new Uint8Array(await response.arrayBuffer()),
    };
  }

  async openStream(request: TransportRequest): Promise<TransportStream> {
    const controller = new AbortController();
    const signal = request.signal
      ? anySignal([request.signal, controller.signal])
      : controller.signal;
    // SSEもfetchで開くのでBearerを付けられる。401の再送は応答ヘッダの時点、
    // つまりchunkを1つも渡す前に限る。読み始めた後の切断はEventStreamの再接続に任せる。
    const response = await this.authorized((token) =>
      fetch(buildUrl(this.baseUrl, request.path, request.params), {
        method: request.method,
        headers: { Accept: 'text/event-stream', ...(request.headers ?? {}), ...bearer(token) },
        signal,
        credentials: 'omit',
        redirect: 'error',
      }),
    );
    const body = response.body;
    return {
      status: response.status,
      header: (name) => response.headers.get(name),
      chunks: () => decodeUtf8Stream(body),
      close: () => controller.abort(),
    };
  }

  /**
   * tokenを付けて送り、401なら1回だけ取り直して再送する。それでも401なら再ログインを始め、
   * 401の応答はそのまま返す（problem+jsonの解釈はApiClientに任せ、ここでは投げない）。
   * 403/503はtokenを替えても変わらないので再送しない。
   */
  private async authorized(
    sendWith: (token: string | null) => Promise<Response>,
  ): Promise<Response> {
    if (!this.auth) return sendWith(null);

    const first = await sendWith(await this.auth.getAccessToken());
    if (first.status !== 401) return first;

    let last = first;
    const refreshed = await this.auth.getAccessToken({ forceRefresh: true });
    if (refreshed) {
      // 再送は1回だけ。繰り返すと、失効したtokenでAPIとCognitoを叩き続けることになる。
      discard(first);
      last = await sendWith(refreshed);
      if (last.status !== 401) return last;
    }
    // Amplifyがログイン済みと判断して遷移を拒んだ場合も、401は画面へ返して原因を見せる。
    // 投げるとApiClientのproblem解釈を通らない。
    await this.auth.reauthenticate().catch(() => undefined);
    return last;
  }

  private headers(request: TransportRequest): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      // null Origin/許可外Originの拒否をAPIが行えるよう、単純requestにしない。
      'X-Requested-With': 'microapp-shell',
      ...(request.headers ?? {}),
    };
    // APIの境界middlewareは、Origin付きの変更要求（POST/PATCH/PUT/DELETE）に
    // Content-Type: application/json（またはIdempotency-Key付きmultipart）を求め、無ければ
    // 403 unsafe_browser_request で拒否する。ブラウザはbodyの無いDELETEにもOriginを付けるので、
    // DELETEはbodyが無くてもJSONとして宣言する。
    if (request.json !== undefined || request.method === 'DELETE') {
      headers['Content-Type'] = 'application/json';
    }
    // ファイル本体は octet-stream。APIの境界はIdempotency-Key付きのときだけ受ける（呼び出し側が付ける）。
    if (request.body !== undefined) {
      headers['Content-Type'] = request.contentType ?? 'application/octet-stream';
    }
    return headers;
  }

  private body(request: TransportRequest): BodyInit | undefined {
    if (request.body !== undefined) return request.body;
    if (request.form) return request.form;
    if (request.json !== undefined) return JSON.stringify(request.json);
    return undefined;
  }
}

function bearer(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** 再送で捨てる応答のbodyを読まずに閉じる。keep-aliveの接続を解放させるため。 */
function discard(response: Response): void {
  response.body?.cancel().catch(() => undefined);
}

async function* decodeUtf8Stream(
  body: ReadableStream<Uint8Array> | null,
): AsyncIterableIterator<string> {
  if (!body) return;
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      const text = decoder.decode(value, { stream: true });
      if (text) yield text;
    }
    const tail = decoder.decode();
    if (tail) yield tail;
  } finally {
    reader.releaseLock();
  }
}

/** AbortSignal.any が無い環境でも動くように自前で束ねる。 */
function anySignal(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();
  const abort = () => controller.abort();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      break;
    }
    signal.addEventListener('abort', abort, { once: true });
  }
  return controller.signal;
}
